import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    User,
    Mail,
    Phone,
    MapPin,
    Calendar,
    Building2,
    DollarSign,
    FileText,
    Edit2,
    Download,
    ChevronRight,
    Home,
    Briefcase,
    BookOpen,
    Award,
    AlertCircle,
    Clock,
    Check,
    X,
    Eye,
    Trash2,
    Star,
    Plus,
    Upload
  } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Reuse GlassCard component
const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 ${className}`}>
    {children}
  </div>
);

const EmployeeDetail = () => {
  const { id } = useParams();
  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeData();
  }, [id]);

  const fetchEmployeeData = async () => {
    setLoading(true);
    try {
      // Simulated API call
      const mockEmployee = {
        id,
        name: 'John Smith',
        position: 'Senior Manager',
        department: 'Operations',
        email: 'john.smith@lyncs.sa',
        phone: '+966 501234567',
        location: 'Riyadh',
        joinDate: '2021-03-15',
        status: 'Active',
        manager: 'Sarah Johnson',
        teamSize: 5,
        salary: {
          current: 15000,
          history: [
            { date: '2021-03', amount: 12000 },
            { date: '2021-09', amount: 13000 },
            { date: '2022-03', amount: 14000 },
            { date: '2022-09', amount: 15000 }
          ]
        },
        performance: {
          rating: 4.2,
          lastReview: '2023-12-15',
          goals: [
            { title: 'Team Development', progress: 75 },
            { title: 'Project Delivery', progress: 90 },
            { title: 'Budget Management', progress: 85 }
          ]
        },
        documents: [
          { name: 'Employment Contract', type: 'PDF', date: '2021-03-15' },
          { name: 'Last Performance Review', type: 'PDF', date: '2023-12-15' },
          { name: 'Training Certificates', type: 'PDF', date: '2023-06-30' }
        ],
        leaves: [
          { type: 'Annual', taken: 15, remaining: 10 },
          { type: 'Sick', taken: 3, remaining: 12 },
          { type: 'Personal', taken: 2, remaining: 3 }
        ],
        training: [
          { name: 'Leadership Development', status: 'Completed', date: '2023-05-15' },
          { name: 'Project Management', status: 'In Progress', date: '2023-12-01' },
          { name: 'Safety Training', status: 'Completed', date: '2023-08-20' }
        ]
      };

      setEmployee(mockEmployee);
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!employee) return null;

  return (
    <div className="min-h-screen bg-transparent p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Link to="/hr" className="hover:text-indigo-600 transition-colors flex items-center">
            <Home className="w-4 h-4 mr-1.5" />
            HR Dashboard
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/hr/employees" className="hover:text-indigo-600 transition-colors">
            Employees
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-indigo-600">{employee.name}</span>
        </div>

        // Add this to the existing EmployeeDetail.js file, inside the main return statement after the Overview tab

{activeTab === 'documents' && (
  <GlassCard className="p-6">
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-lg font-semibold">Documents</h2>
      <button
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
        onClick={() => {/* Add upload functionality */}}
      >
        <Upload className="w-4 h-4 mr-2" />
        Upload Document
      </button>
    </div>
    <div className="space-y-4">
      {employee.documents.map((doc) => (
        <div 
          key={doc.id}
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{doc.name}</h3>
              <p className="text-sm text-gray-500">Uploaded on {new Date(doc.uploadDate).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-500 hover:text-indigo-600 rounded-lg hover:bg-indigo-50">
              <Eye className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-500 hover:text-indigo-600 rounded-lg hover:bg-indigo-50">
              <Download className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-500 hover:text-red-600 rounded-lg hover:bg-red-50">
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  </GlassCard>
)}

{activeTab === 'attendance' && (
  <div className="space-y-6">
    {/* Leave Balance */}
    <GlassCard className="p-6">
      <h2 className="text-lg font-semibold mb-4">Leave Balance</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {employee.attendance.leaves.map((leave, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-500">{leave.type} Leave</h3>
            <div className="mt-2">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Used</span>
                <span className="font-medium">{leave.taken} days</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Remaining</span>
                <span className="font-medium text-indigo-600">{leave.remaining} days</span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-indigo-600 rounded-full"
                  style={{ 
                    width: `${(leave.taken / (leave.taken + leave.remaining)) * 100}%` 
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </GlassCard>

    {/* Attendance Summary */}
    <GlassCard className="p-6">
      <h2 className="text-lg font-semibold mb-4">Monthly Attendance Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-green-700">Present</h3>
            <Check className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-700 mt-2">
            {employee.attendance.present} days
          </p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-red-700">Absent</h3>
            <X className="w-5 h-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-700 mt-2">
            {employee.attendance.absent} days
          </p>
        </div>
        <div className="bg-amber-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-amber-700">Late</h3>
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-2xl font-bold text-amber-700 mt-2">
            {employee.attendance.late} days
          </p>
        </div>
        <div className="bg-indigo-50 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-indigo-700">On Leave</h3>
            <Calendar className="w-5 h-5 text-indigo-600" />
          </div>
          <p className="text-2xl font-bold text-indigo-700 mt-2">
            {employee.attendance.leaves.reduce((acc, curr) => acc + curr.taken, 0)} days
          </p>
        </div>
      </div>
    </GlassCard>
  </div>
)}

{activeTab === 'performance' && (
  <div className="space-y-6">
    {/* Overall Performance */}
    <GlassCard className="p-6">
      <h2 className="text-lg font-semibold mb-4">Performance Overview</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-500">Overall Rating</h3>
            <div className="flex items-center">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-5 h-5 ${
                    star <= employee.performance.rating
                      ? 'text-yellow-400 fill-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
          <p className="text-4xl font-bold text-gray-900">
            {employee.performance.rating.toFixed(1)}/5.0
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last reviewed on {new Date(employee.performance.lastReview).toLocaleDateString()}
          </p>
        </div>
      </div>
    </GlassCard>

    {/* Key Achievements */}
    <GlassCard className="p-6">
      <h2 className="text-lg font-semibold mb-4">Key Achievements</h2>
      <div className="space-y-4">
        {employee.performance.achievements.map((achievement, index) => (
          <div key={index} className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg">
            <Award className="w-5 h-5 text-indigo-600" />
            <span className="text-gray-700">{achievement}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  </div>
)}

{activeTab === 'training' && (
  <GlassCard className="p-6">
    <div className="flex justify-between items-center mb-6">
      <h2 className="text-lg font-semibold">Training & Development</h2>
      <button
        className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
        onClick={() => {/* Add training enrollment functionality */}}
      >
        <Plus className="w-4 h-4 mr-2" />
        Enroll in Training
      </button>
    </div>
    <div className="space-y-4">
      {employee.training.map((course, index) => (
        <div 
          key={index}
          className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
        >
          <div className="flex items-center space-x-4">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              course.status === 'Completed' ? 'bg-green-100' :
              course.status === 'In Progress' ? 'bg-blue-100' :
              'bg-amber-100'
            }`}>
              <BookOpen className={`w-5 h-5 ${
                course.status === 'Completed' ? 'text-green-600' :
                course.status === 'In Progress' ? 'text-blue-600' :
                'text-amber-600'
              }`} />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">{course.name}</h3>
              <p className="text-sm text-gray-500">
                {course.status} • {new Date(course.date).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            {course.status === 'Completed' && (
              <button className="flex items-center space-x-2 text-green-600 bg-green-50 px-3 py-1 rounded-lg">
                <Check className="w-4 h-4" />
                <span>Certificate</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  </GlassCard>
)}

        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{employee.name}</h1>
            <div className="flex items-center gap-4 text-gray-500">
              <span className="flex items-center">
                <Briefcase className="w-4 h-4 mr-1.5" />
                {employee.position}
              </span>
              <span className="flex items-center">
                <MapPin className="w-4 h-4 mr-1.5" />
                {employee.location}
              </span>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button className="px-4 py-2 border border-gray-200 text-gray-700 rounded-xl hover:border-indigo-600 hover:text-indigo-600 transition-all duration-200">
              <Download className="w-4 h-4 mr-2 inline-block" />
              Export Data
            </button>
            <button className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all duration-200">
              <Edit2 className="w-4 h-4 mr-2 inline-block" />
              Edit Profile
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Personal Info */}
        <div className="space-y-6">
          <GlassCard className="p-6">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-20 h-20 bg-indigo-50 rounded-xl flex items-center justify-center">
                <span className="text-2xl font-bold text-indigo-600">
                  {employee.name[0]}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{employee.name}</h3>
                <p className="text-sm text-gray-500">{employee.position}</p>
                <p className="text-sm text-gray-500">{employee.department}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <Mail className="w-4 h-4 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">{employee.email}</span>
              </div>
              <div className="flex items-center">
                <Phone className="w-4 h-4 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">{employee.phone}</span>
              </div>
              <div className="flex items-center">
                <MapPin className="w-4 h-4 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">{employee.location}</span>
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 text-gray-400 mr-3" />
                <span className="text-sm text-gray-600">Joined {employee.joinDate}</span>
              </div>
            </div>
          </GlassCard>

          {/* Leave Balance */}
          <GlassCard className="p-6">
            <h3 className="text-lg font-semibold mb-4">Leave Balance</h3>
            <div className="space-y-4">
              {employee.leaves.map((leave, index) => (
                <div key={index}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{leave.type}</span>
                    <span className="font-medium">{leave.remaining} days remaining</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full">
                    <div
                      className="h-2 bg-indigo-600 rounded-full"
                      style={{ width: `${(leave.taken / (leave.taken + leave.remaining)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Middle Column - Performance & Goals */}
        <div className="space-y-6">
          <GlassCard className="p-6">
            <h3 className="text-lg font-semibold mb-4">Performance Goals</h3>
            <div className="space-y-6">
              {employee.performance.goals.map((goal, index) => (
                <div key={index}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">{goal.title}</span>
                    <span className="font-medium">{goal.progress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full">
                    <div
                      className="h-2 bg-indigo-600 rounded-full transition-all duration-300"
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Salary History */}
          <GlassCard className="p-6">
            <h3 className="text-lg font-semibold mb-4">Salary History</h3>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={employee.salary.history}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#4F46E5" 
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
        </div>

        {/* Right Column - Documents & Training */}
        <div className="space-y-6">
          {/* Documents Section */}
          <GlassCard className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Documents</h3>
              <button className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center">
                <Upload className="w-4 h-4 mr-1" />
                Upload New
              </button>
            </div>
            <div className="space-y-3">
              {employee.documents.map((doc, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center">
                    <FileText className="w-5 h-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                      <p className="text-xs text-gray-500">{doc.date}</p>
                    </div>
                  </div>
                  <button className="p-2 text-gray-400 hover:text-indigo-600">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Training & Development */}
          <GlassCard className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Training & Development</h3>
              <button className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center">
                <Plus className="w-4 h-4 mr-1" />
                Assign Training
              </button>
            </div>
            <div className="space-y-4">
              {employee.training.map((course, index) => (
                <div 
                  key={index}
                  className="p-4 border border-gray-100 rounded-lg hover:border-indigo-100 transition-colors"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-gray-800">{course.name}</h4>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      course.status === 'Completed' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {course.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{course.date}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Quick Actions */}
          <GlassCard className="p-6">
            <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-3">
              <button className="p-3 text-sm text-center bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
                Request Leave
              </button>
              <button className="p-3 text-sm text-center bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
                Performance Review
              </button>
              <button className="p-3 text-sm text-center bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
                Update Details
              </button>
              <button className="p-3 text-sm text-center bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors">
                Training Request
              </button>
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetail;